import styled from "styled-components"

import {
  ButtonProps,
  cardBoxShadow,
  colors,
  getPortalRoot,
  IconButton,
  ListBox,
  Popover,
  PopoverProps,
  spacing,
} from "@minuthq/meatball-ui-react"

import { useTranslate } from "src/i18n/useTranslate"
import OptionsIcon from "src/ui/icons/options-horizontal.svg"
import PenIcon from "src/ui/icons/pen-outlined.svg"
import TrashIcon from "src/ui/icons/trash.svg"

type IClickEvent<E extends HTMLElement = HTMLElement> = React.MouseEvent<
  E,
  MouseEvent
>

interface IMenuItems {
  key: string
  contents: React.ReactNode
  onClick: null | ((e?: IClickEvent<HTMLLIElement>) => void)
  hidden?: boolean
  disabled?: boolean
  tooltip?: {
    title: string | null
    placement?: "top" | "bottom" | "left" | "right"
  }
}

export function MoreButton({
  onEdit,
  onDelete,
  menuItems,
  editLabel,
  deleteLabel,
  showDeleteIcon = true,
  disabled,
  placement = "bottom",
  buttonVariant = "minimal",
  buttonSize = "small",
}: {
  onEdit?: () => void
  onDelete?: () => void
  menuItems?: IMenuItems[]
  editLabel?: React.ReactNode
  deleteLabel?: React.ReactNode
  showDeleteIcon?: boolean
  disabled?: boolean
  placement?: PopoverProps["placement"]
  buttonVariant?: ButtonProps["variant"]
  buttonSize?: ButtonProps["size"]
}) {
  const { t, langKeys } = useTranslate()
  const popoverId = "more-button"

  return (
    <>
      <Popover
        trigger={
          <IconButton
            Icon={OptionsIcon}
            aria-label="more-button"
            aria-describedby={popoverId}
            disabled={disabled}
            variant={buttonVariant}
            size={buttonSize}
          />
        }
        placement={placement}
        offset={10}
        root={getPortalRoot() ?? undefined}
      >
        {({ closeMenu }) => (
          <PopoverContent>
            <ListBox
              items={[
                ...(menuItems
                  ?.filter((item) => !item.hidden)
                  .map((item) => ({
                    id: item.key,
                    content: item.contents,
                    onClick: () => {
                      item.onClick?.()
                      closeMenu()
                    },
                    disabled: item.disabled,
                    tooltip: item.tooltip
                      ? {
                          title: item.tooltip.title || "",
                          placement: item.tooltip.placement,
                        }
                      : undefined,
                  })) || []),
                ...(!!onEdit
                  ? [
                      {
                        id: "edit",
                        prefix: <PenIcon height="16" width="16" />,
                        content: editLabel || t(langKeys.edit),
                        onClick: () => {
                          onEdit()
                          closeMenu()
                        },
                      },
                    ]
                  : []),
                ...(!!onDelete
                  ? [
                      {
                        id: "delete",
                        prefix: showDeleteIcon && (
                          <TrashIcon
                            height="16"
                            width="16"
                            fill={colors.systemErrorDark}
                          />
                        ),
                        content: deleteLabel || t(langKeys.remove),
                        onClick: () => {
                          onDelete()
                          closeMenu()
                        },
                      },
                    ]
                  : []),
              ]}
            />
          </PopoverContent>
        )}
      </Popover>
    </>
  )
}

const PopoverContent = styled.div`
  background-color: ${colors.neutralLight};
  padding-block: ${spacing.XS};
  border-radius: 0.5rem;
  border: 1px solid ${colors.neutralDark};
  ${cardBoxShadow}
`
